import { Organization } from "fhir/r5";

export const NOTE_EXTENSION_URL = "http://hl7.org/fhir/StructureDefinition/note";

export interface OrganizationNote {
    id: string;
    text: string;
    time?: string;
    author?: string;
}

export interface OrganizationNoteServiceConfig {
    fhirBaseUrl: string;
}

type OrganizationExtension = NonNullable<Organization["extension"]>[number];

type NoteExtension = OrganizationExtension & {
    valueAnnotation?: {
        text?: string;
        time?: string;
        authorString?: string;
        authorReference?: {
            reference?: string;
            display?: string;
        };
    };
};

class OrganizationNoteService {
    private fhirBaseUrl: string;

    constructor(config: OrganizationNoteServiceConfig) {
        this.fhirBaseUrl = config.fhirBaseUrl.replace(/\/$/, "");
    }

    async getNotes(organizationReference: string): Promise<OrganizationNote[]> {
        const organization = await this.readOrganization(organizationReference);
        return this.extractNotes(organization);
    }

    async addNote(
        organizationReference: string,
        text: string
    ): Promise<OrganizationNote[]> {
        const normalizedText = text.trim();

        if (!normalizedText) {
            throw new Error("La note ne peut pas être vide.");
        }

        const organization = await this.readOrganization(organizationReference);
        const noteExtension: NoteExtension = {
            url: NOTE_EXTENSION_URL,
            valueAnnotation: {
                time: new Date().toISOString(),
                text: normalizedText
            }
        };

        const updatedOrganization: Organization = {
            ...organization,
            extension: [
                ...(organization.extension ?? []),
                noteExtension
            ]
        };

        const response = await fetch(this.getOrganizationUrl(organizationReference), {
            method: "PUT",
            headers: {
                Accept: "application/fhir+json",
                "Content-Type": "application/fhir+json"
            },
            body: JSON.stringify(updatedOrganization)
        });

        if (!response.ok) {
            throw new Error(await this.readError(response));
        }

        // Relecture volontaire : elle récupère la version réellement persistée par
        // le serveur et évite de supposer que la réponse du PUT contient la ressource.
        return this.getNotes(organizationReference);
    }

    private async readOrganization(organizationReference: string): Promise<Organization> {
        const response = await fetch(this.getOrganizationUrl(organizationReference), {
            headers: {
                Accept: "application/fhir+json"
            }
        });

        if (!response.ok) {
            throw new Error(await this.readError(response));
        }

        const resource = await response.json() as Organization;

        if (resource.resourceType !== "Organization") {
            throw new Error("La ressource FHIR récupérée n'est pas une Organization.");
        }

        return resource;
    }

    private extractNotes(organization: Organization): OrganizationNote[] {
        const extensions = (organization.extension ?? []) as NoteExtension[];

        return extensions
            .map((extension, index): OrganizationNote | undefined => {
                if (extension.url !== NOTE_EXTENSION_URL) {
                    return undefined;
                }

                const annotation = extension.valueAnnotation;
                const text = annotation?.text?.trim();

                if (!text) {
                    return undefined;
                }

                return {
                    id: extension.id ?? `${annotation?.time ?? "undated"}-${index}`,
                    text,
                    time: annotation?.time,
                    author: annotation?.authorString ??
                        annotation?.authorReference?.display ??
                        annotation?.authorReference?.reference
                };
            })
            .filter((note): note is OrganizationNote => !!note)
            .sort((left, right) => {
                if (!left.time && !right.time) return 0;
                if (!left.time) return 1;
                if (!right.time) return -1;
                return right.time.localeCompare(left.time);
            });
    }

    private getOrganizationUrl(organizationReference: string): string {
        const normalizedReference = organizationReference.replace(/^\//, "");
        const resourcePath = normalizedReference.startsWith("Organization/")
            ? normalizedReference
            : `Organization/${normalizedReference}`;

        return `${this.fhirBaseUrl}/${resourcePath}`;
    }

    private async readError(response: Response): Promise<string> {
        const body = await response.text();
        return body || `Erreur FHIR ${response.status}`;
    }
}

export default OrganizationNoteService;
